import { Connect, ConnectInstaller } from "@ibm-aspera/connect-sdk-js";
import {
  ConnectError,
  ConnectSpec,
  DataTransfer,
  DragDropListener,
  StartTransferOutput,
  TransferSpec,
} from "@ibm-aspera/connect-sdk-js/dist/esm/core/types";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { Alert, AlertTitle, Box, Button, Card, Divider, Stack, styled, Tooltip, Typography } from "@mui/material";
import { createRef, forwardRef, useEffect, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { cacheService, fileService, ingestService, mediaService, setupService } from "../../../Core/Api/services";
import { AsperaTransferDirection } from "../../../Core/Api/Types/aspera-transfer-direction";
import { AsperaUploadMediaFilesDto } from "../../../Core/Api/Types/db-file-infos";
import { UploadCardFormatDto } from "../../../Core/Api/Types/db-format-infos";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ProviderAndAsperaConfiguration } from "../../../Core/Api/Types/provider-and-aspera-configuration";
import { GetUploadCardReadyToUploadIndexes } from "../../../Redux/Reducers/redux-slice-aspera-upload";
import { useAppSelector } from "../../../Redux/redux-store";
import { GetFileExtensionFromPath, GetFileNameFromPath, UserHasLicence } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import UploadCard, { UploadCardRef, UploadContext } from "../../Cards/UploadCard/upload-card";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { Licences } from "../../../Core/Api/Types/licences";
import { useTranslation } from "react-i18next";

const fileSpecifications = {
  Video: {
    ext: {
      Mezz: ["mov", "mxf", "mpg", "mpeg"],
      Raw: ["mov", "mxf", "mpg", "mpeg", "mp4", "mkv", "avi", "m2v", "m4v", "gxf"],
    },
    isFolder: false,
  },
  Audio: {
    ext: {
      Mezz: ["mov", "wav"],
      Raw: ["mov", "wav", "bwav", "mp3", "m3a", "m4a", "aif", "aiff", "ac3"],
    },
    isFolder: false,
  },
  Subtitle: {
    ext: {
      Mezz: ["stl", "pac", "itt", "ttml", "xml", "cap", "dfxp", "scc"],
      Raw: ["stl", "pac", "itt", "ttml", "xml", "cap", "dfxp", "srt", "scc"],
    },
    isFolder: false,
  },
  Image: {
    ext: {
      Mezz: ["jpg", "jpeg", "png", "lsr"],
      Raw: ["jpg", "jpeg", "png", "lsr"],
    },
    isFolder: false,
  },
  Generic: {
    ext: {
      Mezz: ["mxf", "xml", "zip", "tar", "ASSETMAP", "VOLINDEX"],
      Raw: ["mxf", "xml", "zip", "tar", "ASSETMAP", "VOLINDEX"],
    },
    isFolder: true,
  },
  ImfPackage: {
    ext: {
      Mezz: ["mxf", "xml", "zip", "tar", "ASSETMAP", "VOLINDEX"],
      Raw: ["mxf", "xml", "zip", "tar", "ASSETMAP", "VOLINDEX"],
      Imf: ["mxf", "xml", "ASSETMAP", "VOLINDEX"],
    },
    isFolder: true,
  },
  DcpPackage: {
    ext: {
      Mezz: ["mxf", "xml", "zip", "tar", "ASSETMAP", "VOLINDEX"],
      Raw: ["mxf", "xml", "zip", "tar", "ASSETMAP", "VOLINDEX"],
      Dcp: ["mxf", "xml", "ASSETMAP", "VOLINDEX"],
    },
    isFolder: true,
  },
  Folder: {
    ext: {
      Mezz: [
        "mxf",
        "xml",
        "zip",
        "tar",
        "ASSETMAP",
        "VOLINDEX",
        "jpg",
        "jpeg",
        "png",
        "lsr",
        "mov",
        "mxf",
        "mpg",
        "mpeg",
        "stl",
        "pac",
        "itt",
        "ttml",
        "xml",
        "cap",
        "dfxp",
        "scc",
      ],
      Drive: [
        "mxf",
        "xml",
        "zip",
        "tar",
        "ASSETMAP",
        "VOLINDEX",
        "jpg",
        "jpeg",
        "png",
        "lsr",
        "mov",
        "mxf",
        "mpg",
        "mpeg",
        "mp4",
        "mkv",
        "avi",
        "m2v",
        "m4v",
        "gxf",
        "stl",
        "pac",
        "itt",
        "ttml",
        "xml",
        "cap",
        "dfxp",
        "srt",
        "scc",
      ],
    },
    isFolder: true,
  },
  GraphSkeleton: {
    ext: {
      Mezz: [
        "mxf",
        "xml",
        "zip",
        "tar",
        "ASSETMAP",
        "VOLINDEX",
        "jpg",
        "jpeg",
        "png",
        "lsr",
        "mov",
        "mxf",
        "mpg",
        "mpeg",
        "stl",
        "pac",
        "itt",
        "ttml",
        "xml",
        "cap",
        "dfxp",
        "scc",
      ],
      Drive: [
        "mxf",
        "xml",
        "zip",
        "tar",
        "ASSETMAP",
        "VOLINDEX",
        "jpg",
        "jpeg",
        "png",
        "lsr",
        "mov",
        "mxf",
        "mpg",
        "mpeg",
        "mp4",
        "mkv",
        "avi",
        "m2v",
        "m4v",
        "gxf",
        "stl",
        "pac",
        "itt",
        "ttml",
        "xml",
        "cap",
        "dfxp",
        "srt",
        "scc",
      ],
      Raw: [
        "mxf",
        "xml",
        "zip",
        "tar",
        "ASSETMAP",
        "VOLINDEX",
        "jpg",
        "jpeg",
        "png",
        "lsr",
        "mov",
        "mxf",
        "mpg",
        "mpeg",
        "mp4",
        "mkv",
        "avi",
        "m2v",
        "m4v",
        "gxf",
        "stl",
        "pac",
        "itt",
        "ttml",
        "xml",
        "cap",
        "dfxp",
        "srt",
        "scc",
      ],
    },
    isFolder: true,
  },
  ImageSequence: {
    ext: {
      Mezz: ["dpx", "tif", "tiff"],
      Raw: ["dpx", "tif", "tiff"],
    },
    isFolder: true,
  },
};

export type ModalAsperaUploadElementProps = {
  assetId: number;
  assetParentName: string;
  assetName: string;
  assetType: ObjectType;
  assetAnnexFormatIds: number[];
  assetIsHdr: boolean;
  assetClassification: ObjectClass;
  assetProviderId: number;
  showWarning?: boolean;
  useToken?: boolean;
};

type FileObjects = DataTransfer["dataTransfer"]["files"];

export type ModalAsperaUploadProps = CommonModalProps<ModalAsperaUploadElementProps>;

/* TODOS :
    - Confirm modal then do upload ?
*/

// Idées :
// Peut etre faire une ptite modal "attention" lorsqu'on veut fermer la modal d'upload et qu'un upload http est en cours (je pense que ca casse, a vérifier)

const ModalAsperaUpload = forwardRef<any, ModalAsperaUploadProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    assetId,
    assetParentName,
    assetName,
    assetType,
    assetAnnexFormatIds,
    assetIsHdr,
    assetClassification,
    assetProviderId,
    closeModal,
    showWarning = false,
    useToken = false,
  } = props;
  const asperaDropZoneId = "aspera-drop-file-zone";
  // change this to make annex files mandatory
  const isAnnexFilesMandatory = false;
  // #region states
  // aspera drop zone
  const [isAsperaDropZoneActive, setIsDropZoneActive] = useState(false);
  const [asperaDropzoneError, setAsperaDropzoneError] = useState<{ error: string; detail: string } | undefined>(
    undefined
  );
  const [asperaFilesToUpload, setAsperaFilesToUpload] = useState<FileObjects>([]);
  const [aspSettings, setAspSettings] = useState<ProviderAndAsperaConfiguration[]>([]);
  // annex files
  const [annexFilesFormats, setAnnexFilesFormats] = useState<UploadCardFormatDto[]>([]);
  const [alreadyUploadedFiles, setAlreadyUploadedFiles] = useState<AsperaUploadMediaFilesDto[]>([]);
  const [uploadCardsRefs, setUploadCardsRefs] = useState<React.RefObject<UploadCardRef | null>[]>([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const annexFilesIndexReadyToUpload = useAppSelector((state) => GetUploadCardReadyToUploadIndexes(state));
  // #endregion

  const enqueueSnackbar = useCustomSnackbar();

  // #region handle aspera
  // useMemo for Aspera Connect and ConnectInstaller avoid connectToReinitialize on component re-rendering
  const connect = useMemo(
    () =>
      new Connect({
        dragDropEnabled: true,
      }),
    []
  );
  const connectInstaller = useMemo(() => new ConnectInstaller(), []);

  // statusListener event function, manages install, update of aspera on OS and connect status of AsperaConnect
  const connectStatusEventListener = (eventType: string, data: string) => {
    if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.INITIALIZING) {
      connectInstaller.showLaunching();
    } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.EXTENSION_INSTALL) {
      connectInstaller.showExtensionInstall();
    } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.FAILED) {
      connectInstaller.showDownload();
    } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.OUTDATED) {
      connectInstaller.showUpdate();
    } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.RUNNING) {
      connectInstaller.connected();
    }
  };

  // function that init connect driver
  const initConnect = () => {
    // we add the connect listener to connect on status
    connect.addEventListener(Connect.EVENT.STATUS, connectStatusEventListener);
    // init session
    connect.initSession();
    // configure dragDrop props and callback
    connect.setDragDropTargets(
      `#${asperaDropZoneId}`,
      {
        dragEnter: true,
        dragLeave: true,
        dragOver: true,
        drop: true,
      },
      (dragDropEvent) => handleAsperaDrop(dragDropEvent)
    );
  };

  // check file function, tells us if file is ok or not based on asset props and file specs. Manages the error text too
  const checkFiles = (files: FileObjects): boolean => {
    // if more than 1 file and asset is not generic and not hdr
    if (
      files.length > 1 &&
      assetType !== ObjectType.Generic &&
      !assetIsHdr &&
      cacheService.GetPlatformMode() !== PlatformMode.Browser
    ) {
      setAsperaDropzoneError({
        error: t("modal:modalAsperaUpload.dropZoneError.multipleFileError"),
        detail: t("modal:modalAsperaUpload.dropZoneError.detailMultipleFileError"),
      });
      return false;
    }

    // if asset is hdr and file length is not 2 (one video file and one hdr file)
    if (assetIsHdr && files.length !== 2 && assetType === ObjectType.Video) {
      setAsperaDropzoneError({
        error: t("modal:modalAsperaUpload.dropZoneError.hdrFileError"),
        detail: t("modal:modalAsperaUpload.dropZoneError.detailHdrFileError"),
      });
      return false;
    }

    // for all files
    let ok: boolean = true;

    for (let i = 0; i < files.length; i++) {
      // directory drop is not allowed
      if (
        files[i].type === "inode/directory" &&
        fileSpecifications[assetType].isFolder !== true &&
        !(UserHasLicence(Licences.ImageSequenceProxyTranscode) && assetType === ObjectType.ImageSequence) &&
        cacheService.GetPlatformMode() !== PlatformMode.Browser
      ) {
        setAsperaDropzoneError({
          error: t("modal:modalAsperaUpload.dropZoneError.directoryDropError"),
          detail: t("modal:modalAsperaUpload.dropZoneError.detailDirectoryDropError"),
        });
        ok = false;
        break;
      }

      // dcpPackage
      if (
        (files[i].name.indexOf("VOLINDEX") > 0 || files[i].name.indexOf("ASSETMAP") > 0) &&
        assetType === "DcpPackage"
      ) {
        continue;
      }

      // xml type for hdr
      if (assetIsHdr && files[i].type === "application/xml") {
        continue;
      }

      // get extension
      const fileExtension = GetFileExtensionFromPath(files[i].name);

      // if file extension isn't correct for asset type and class
      if (
        fileSpecifications[assetType] !== undefined &&
        fileSpecifications[assetType]?.ext[assetClassification]?.indexOf(fileExtension) < 0 &&
        files[i].type !== "inode/directory"
      ) {
        setAsperaDropzoneError({
          error: t("modal:modalAsperaUpload.dropZoneError.fileExtensionError"),
          detail: t("modal:modalAsperaUpload.dropZoneError.detailFileExtensionError", {
            fileExtension,
            assetType,
            assetClassification,
          }),
        });
        ok = false;
        break;
      }
    }

    // else it's good, we return true
    if (ok) setAsperaDropzoneError(undefined);

    return ok;
  };

  // function that manages the aspera dragDrop events, basically if checkFiles is ok is sets files to upload
  const handleAsperaDrop: DragDropListener = (dragDropEvent) => {
    if (dragDropEvent.event.type === "dragenter") {
      setIsDropZoneActive(true);
    } else if (dragDropEvent.event.type === "dragleave") {
      setIsDropZoneActive(false);
    } else if (dragDropEvent.event.type === "drop") {
      setIsDropZoneActive(false);
      if (!dragDropEvent.files || !dragDropEvent.files.dataTransfer.files.length) {
        console.warn("No files in drop event");
        return;
      }
      if (checkFiles(dragDropEvent.files.dataTransfer.files)) {
        console.debug("Files are ok, setting them to upload");
        setAsperaFilesToUpload(dragDropEvent.files.dataTransfer.files);
      }
    }
  };

  // function that handle remove of file for aspera
  const handleRemoveAsperaFile = (fileName: string) => {
    setAsperaFilesToUpload([...asperaFilesToUpload.filter((x) => x.name !== fileName)]);
  };

  // function that returns the aspSettings needed to upload via aspera
  const getAspSettings = (): ProviderAndAsperaConfiguration | undefined => {
    // if there's only one, take it (the one linked to user)
    if (aspSettings.length === 1) return aspSettings[0];
    // if we are on drive, take first with no affinity check
    if (assetClassification === ObjectClass.Drive) return aspSettings.find((x) => x.provider?.disableAffinityCheck);
    // else find the one linked to asset
    return aspSettings.find((x) => x.provider && x.provider.id === assetProviderId);
  };

  // on transfer success (transfer is starting)
  const handleTransferSuccess = (
    response: StartTransferOutput,
    fileNames?: string[],
    token?: string,
    objectId?: number
  ) => {
    setAsperaFilesToUpload([]);

    if (!useToken) {
      enqueueSnackbar(t("alert:success.asperaUpload"), "success");
      setLoadingButton(false);
    } else {
      if (!token || !objectId) {
        enqueueSnackbar(t("alert:error.missingTokenUpload"), "error", true);
        return;
      }

      let uuid = response.transfer_specs[0].uuid;

      fileService.UploadAsperaFileTrack(objectId, token, uuid, fileNames ?? []).then((responseTrack) => {
        if (responseTrack.success) {
          enqueueSnackbar(t("alert:success.asperaUpload"), "success");
        } else {
          enqueueSnackbar(
            t("alert:error.failedToTrackAsperaUpload", { message: responseTrack.message }),
            "error",
            true
          );
          connect.stopTransfer(uuid).then(() => {
            connect.removeTransfer(uuid);
          });
        }

        setLoadingButton(false);
      });
    }
  };

  // on transfer failed (transfer can't start)
  const handleTransferFailed = (error: ConnectError) => {
    enqueueSnackbar(error.error.user_message, "error");
    setLoadingButton(false);
  };

  // function that manages the aspera upload
  const handleAsperaUpload = () => {
    // if no file, don't process
    if (asperaFilesToUpload.length === 0) return;
    // get aspera settings
    const aspSettings = getAspSettings();
    // check if provider exists
    if (!aspSettings || !aspSettings.provider || !aspSettings.provider || !aspSettings.provider.asperaAccount) {
      enqueueSnackbar(t("alert:error.failToFindProvider"), "error");
      return;
    }

    setLoadingButton(true);

    const connectSpecs: ConnectSpec = {
      allow_dialogs: false,
    };

    let transferSpecs: TransferSpec | null = null;

    if (!useToken) {
      // upload configuration
      transferSpecs = {
        paths: asperaFilesToUpload.map((x) => ({ source: x.name }))!,
        remote_host: aspSettings.provider.asperaAccount.asperaHost!,
        remote_user: aspSettings.provider.asperaAccount.asperaLogin!,
        remote_password: aspSettings.provider.asperaAccount.asperaPassword!,
        direction: AsperaTransferDirection.send,
        target_rate_kbps: 155000,
        resume: "sparse_checksum",
        ssh_port: aspSettings.provider.asperaAccount.asperaSshPort!,
        fasp_port: aspSettings.provider.asperaAccount.asperaTcpPort!,
        destination_root: `/${assetId}`,
        create_dir: true,
      };

      // start transfer
      connect.startTransfer(transferSpecs, connectSpecs, {
        success: handleTransferSuccess,
        error: handleTransferFailed,
      });
    } else {
      fileService
        .UploadAsperaFile(
          assetId,
          asperaFilesToUpload.map((file) => file.name)
        )
        .then((response) => {
          if (response.success) {
            //enqueueSnackbar("Aspera token successful", "success");
            transferSpecs = response.transferSpec as TransferSpec;

            // start transfer
            connect.startTransfer(transferSpecs, connectSpecs, {
              success: (sto: StartTransferOutput) =>
                handleTransferSuccess(
                  sto,
                  asperaFilesToUpload.map((file) => file.name),
                  response.token ?? undefined,
                  response.objectId
                ),
              error: handleTransferFailed,
            });
          } else {
            enqueueSnackbar(t("alert:error.failedToCreateUploadToken", { message: response.message }), "error");
          }
        });
    }
  };
  // #endregion

  const handleAnnexFileUpload = () => {
    uploadCardsRefs.forEach((ref) => {
      ref.current?.performUpload();
    });
  };

  const handleUpload = () => {
    handleAsperaUpload();
    handleAnnexFileUpload();
  };

  const handleCancelClick = () => {
    closeModal(); // TODO : clean redux on close
  };

  // useEffect fetch data on component first rendering. It also initialize asperaConnect
  useEffect(() => {
    Promise.all([
      setupService.GetFormatsForAsperaAnnexUpload(assetAnnexFormatIds),
      mediaService.GetMediaFilesForAsperaUploadModal(assetId),
      ingestService.GetAsperaSettingsForRequestUpload(),
    ]).then(([formats, medias, asperaSettings]) => {
      initConnect();
      setAlreadyUploadedFiles(medias);
      setAnnexFilesFormats(formats);
      setUploadCardsRefs(formats.map(() => createRef()));
      setAspSettings(asperaSettings);
    });
  }, []);

  return (
    <Modal
      title={t("modal:title.uploadOn", { name: assetParentName ? `${assetParentName} - ${assetName}` : assetName })}
      reference={ref}
      closeModal={closeModal}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={handleCancelClick}>
            {t("common:cancel")}
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={handleUpload}
            disabled={
              asperaFilesToUpload.length === 0 ||
              (annexFilesFormats.length > 0 &&
                isAnnexFilesMandatory &&
                annexFilesIndexReadyToUpload.length !== annexFilesFormats.length)
            }
            loading={loadingButton}
          >
            {t("button:performUpload")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        {showWarning && (
          <Alert icon={false} severity="warning">
            <AlertTitle sx={{ fontWeight: 700 }}>{t("alert:warning.httpUploadLimitation")}</AlertTitle>
            <Typography fontWeight={400}>{t("modal:modalAsperaUpload.detail.dropFileAgain")}</Typography>
          </Alert>
        )}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h5">{t("modal:modalAsperaUpload.detail.asperaUploadField")}</Typography>
        </Stack>
        <UploadSlot
          id={asperaDropZoneId}
          isDragReject={false /* TODO : handle error*/}
          error={asperaDropzoneError !== undefined}
          active={isAsperaDropZoneActive}
        >
          <Box
            // trick to prevent dragleave from firing when pointer enters a children, pretty useful. Need to be set on childrens, applied to childrens of children
            // https://stackoverflow.com/a/18582960
            sx={{ pointerEvents: "none" }}
          >
            <Stack alignItems="center">
              <Typography variant="subtitle2" component="span" fontWeight={600}>
                {asperaDropzoneError !== undefined ? asperaDropzoneError.error : t("common:dragYourFileHere")}
              </Typography>
              {asperaDropzoneError && (
                <Typography variant="body2" component="span" color="text.secondary" mt={1}>
                  {asperaDropzoneError.detail}
                </Typography>
              )}
              {cacheService.GetPlatformMode() !== PlatformMode.Browser && (
                <Typography variant="body2" component="span" color="text.secondary" mt={1}>
                  {t("modal:modalAsperaUpload.detail.acceptedTypes", {
                    types: fileSpecifications[assetType]?.ext[assetClassification]
                      ?.map((ext: string) => `.${ext}`)
                      .join(" "),
                  })}
                </Typography>
              )}
            </Stack>
          </Box>
        </UploadSlot>
        {asperaFilesToUpload.length > 0 && (
          <Stack spacing={1}>
            {asperaFilesToUpload.map((file, index) => (
              <AsperaUploadCard
                key={index}
                title={GetFileNameFromPath(file.name) || file.name}
                onDelete={() => handleRemoveAsperaFile(file.name)}
              />
            ))}
          </Stack>
        )}
      </Stack>
      {annexFilesFormats.length > 0 && uploadCardsRefs.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5">{t("modal:modalAsperaUpload.detail.annexFilesUpload")}</Typography>
              <Tooltip
                placement="right"
                arrow
                title={<Typography variant="body1">{t("modal:body.uploadFilesViaHttp")}</Typography>}
              >
                <HelpOutlineOutlinedIcon />
              </Tooltip>
            </Stack>
            {annexFilesFormats.map((format, index) => {
              const alreadyUploadedFile = alreadyUploadedFiles.find((x) => x.formatId === format.id);
              return (
                <UploadCard
                  key={index}
                  ref={uploadCardsRefs[index] as any}
                  title={format.name}
                  format={format}
                  objectId={assetId}
                  // TODO : keep this here or in upload card ?
                  dropZoneOptions={{
                    maxSize: format.maxFileSizeMb * 1024 * 1024,
                    maxFiles: 1,
                  }}
                  small
                  userHasRightToDelete={format.userHasRighToDelete}
                  alreadyUploadedFile={alreadyUploadedFile}
                  context={UploadContext.MediaFile}
                />
              );
            })}
          </Stack>
        </>
      )}
    </Modal>
  );
});

export default ModalAsperaUpload;

const UploadSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "sx" && prop !== "error" && prop !== "active" && prop !== "isDragReject",
})<{
  error: boolean;
  isDragReject: boolean;
  active: boolean;
}>(({ theme, error, isDragReject, active }) => ({
  position: "relative",
  minHeight: 164,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  border: `1px dashed ${theme.palette.backgroundAlpha.borderFocus}`,
  borderRadius: 12,
  padding: `${theme.spacing(3.5)} ${theme.spacing(4)}`,
  transition: "all .3s",

  label: {
    cursor: "pointer",
  },

  "&:hover": {
    background: theme.palette.backgroundAlpha.hover,
  },

  "&:dragenter": {
    background: theme.palette.backgroundAlpha.hover,
  },

  ...(active && {
    borderColor: theme.palette.backgroundAlpha.borderFocus,
    background: theme.palette.backgroundAlpha.borderFocus,
  }),
  ...((isDragReject || error) && {
    borderColor: theme.palette.error.main,
  }),
  ...(isDragReject && {
    background: theme.palette.error.light,
  }),

  ".error-text": {
    position: "absolute",
    bottom: theme.spacing(1),
    opacity: isDragReject ? 1 : 0,
    transition: "opacity .3s",
    pointerEvents: "none",
  },
}));

interface AsperaUpladCardProps {
  title: string;
  onDelete: () => void;
}

const AsperaUploadCard: React.FC<AsperaUpladCardProps> = (props) => {
  const { t } = useTranslation();
  const { title, onDelete } = props;
  return (
    <Card variant="outlined" sx={{ px: 2, py: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body1" fontWeight={500}>
          {title}
        </Typography>
        <Button
          size="small"
          color="error"
          variant="outlined"
          startIcon={<HighlightOffOutlinedIcon />}
          onClick={onDelete}
        >
          {t("common:remove")}
        </Button>
      </Stack>
    </Card>
  );
};
